import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts';
import { Settings } from 'lucide-react';
import { mainAccountValue, futuresAccountValue, earnBalance, marginPositionsUpnl, futuresUnrealizedPnl } from '../../utils/portfolioData';
interface PortfolioChartProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
// Get the actual account value based on account type
const getAccountValue = (accountType: string): number => {
  switch (accountType) {
    case 'main':
      return mainAccountValue;
    case 'futures':
      return futuresAccountValue;
    case 'earn':
      return earnBalance;
    default:
      return mainAccountValue;
  }
};
// Get the UP&L based on account type
const getAccountUpnl = (accountType: string): number => {
  switch (accountType) {
    case 'main':
      return marginPositionsUpnl;
    case 'futures':
      return futuresUnrealizedPnl;
    default:
      return 0;
  }
};
// Generate chart data based on actual account values
const generateChartData = (accountType: string) => {
  const baseValue = getAccountValue(accountType);
  const upnl = getAccountUpnl(accountType);
  // Create realistic historical data based on current value
  const variance = baseValue * 0.02; // 2% variance for chart movement
  const baseData = {
    main: {
      '1W': Array.from({
        length: 7
      }, (_, i) => ({
        time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
        value: baseValue - variance + variance * 2 * i / 6 + (Math.random() - 0.5) * variance * 0.3,
        upnl: upnl * (0.7 + Math.random() * 0.6),
        timestamp: `Day ${i + 1}`
      })),
      '1M': Array.from({
        length: 30
      }, (_, i) => ({
        time: `Day ${i + 1}`,
        value: baseValue - variance * 1.5 + variance * 3 * i / 29 + (Math.random() - 0.5) * variance * 0.4,
        upnl: upnl * (0.5 + Math.random() * 1.0),
        timestamp: `Day ${i + 1}`
      })),
      '3M': Array.from({
        length: 12
      }, (_, i) => ({
        time: `Week ${i + 1}`,
        value: baseValue - variance * 2 + variance * 4 * i / 11 + (Math.random() - 0.5) * variance * 0.5,
        upnl: upnl * (0.3 + Math.random() * 1.4),
        timestamp: `Week ${i + 1}`
      })),
      '6M': Array.from({
        length: 24
      }, (_, i) => ({
        time: `Week ${i + 1}`,
        value: baseValue - variance * 3 + variance * 6 * i / 23 + (Math.random() - 0.5) * variance * 0.6,
        upnl: upnl * (0.2 + Math.random() * 1.6),
        timestamp: `Week ${i + 1}`
      })),
      '1Y': Array.from({
        length: 12
      }, (_, i) => ({
        time: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
        value: baseValue - variance * 4 + variance * 8 * i / 11 + (Math.random() - 0.5) * variance * 0.7,
        upnl: upnl * (0.1 + Math.random() * 1.8),
        timestamp: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]
      })),
      All: Array.from({
        length: 24
      }, (_, i) => ({
        time: `M${i + 1}`,
        value: baseValue * 0.4 + baseValue * 0.6 * i / 23 + (Math.random() - 0.5) * variance,
        upnl: upnl * (Math.random() * 2),
        timestamp: `Month ${i + 1}`
      }))
    },
    futures: {
      '1W': Array.from({
        length: 7
      }, (_, i) => {
        const futuresValue = getAccountValue('futures');
        const futuresVariance = futuresValue * 0.03;
        return {
          time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
          value: futuresValue - futuresVariance + futuresVariance * 2 * i / 6 + (Math.random() - 0.5) * futuresVariance * 0.3,
          timestamp: `Day ${i + 1}`
        };
      }),
      '1M': Array.from({
        length: 30
      }, (_, i) => {
        const futuresValue = getAccountValue('futures');
        const futuresVariance = futuresValue * 0.03;
        return {
          time: `Day ${i + 1}`,
          value: futuresValue - futuresVariance * 1.5 + futuresVariance * 3 * i / 29 + (Math.random() - 0.5) * futuresVariance * 0.4,
          timestamp: `Day ${i + 1}`
        };
      }),
      '3M': Array.from({
        length: 12
      }, (_, i) => {
        const futuresValue = getAccountValue('futures');
        const futuresVariance = futuresValue * 0.03;
        return {
          time: `Week ${i + 1}`,
          value: futuresValue - futuresVariance * 2 + futuresVariance * 4 * i / 11 + (Math.random() - 0.5) * futuresVariance * 0.5,
          timestamp: `Week ${i + 1}`
        };
      }),
      '6M': Array.from({
        length: 24
      }, (_, i) => {
        const futuresValue = getAccountValue('futures');
        const futuresVariance = futuresValue * 0.03;
        return {
          time: `Week ${i + 1}`,
          value: futuresValue - futuresVariance * 3 + futuresVariance * 6 * i / 23 + (Math.random() - 0.5) * futuresVariance * 0.6,
          timestamp: `Week ${i + 1}`
        };
      }),
      '1Y': Array.from({
        length: 12
      }, (_, i) => {
        const futuresValue = getAccountValue('futures');
        const futuresVariance = futuresValue * 0.03;
        return {
          time: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
          value: futuresValue - futuresVariance * 4 + futuresVariance * 8 * i / 11 + (Math.random() - 0.5) * futuresVariance * 0.7,
          timestamp: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]
        };
      }),
      All: Array.from({
        length: 24
      }, (_, i) => {
        const futuresValue = getAccountValue('futures');
        const futuresVariance = futuresValue * 0.03;
        return {
          time: `M${i + 1}`,
          value: futuresValue * 0.4 + futuresValue * 0.6 * i / 23 + (Math.random() - 0.5) * futuresVariance,
          timestamp: `Month ${i + 1}`
        };
      })
    },
    earn: {
      '1W': Array.from({
        length: 7
      }, (_, i) => {
        const earnValue = getAccountValue('earn');
        return {
          time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
          value: earnValue - 50 + i * 8,
          timestamp: `Day ${i + 1}`
        };
      }),
      '1M': Array.from({
        length: 30
      }, (_, i) => {
        const earnValue = getAccountValue('earn');
        return {
          time: `Day ${i + 1}`,
          value: earnValue - 200 + i * 7,
          timestamp: `Day ${i + 1}`
        };
      }),
      '3M': Array.from({
        length: 12
      }, (_, i) => {
        const earnValue = getAccountValue('earn');
        return {
          time: `Week ${i + 1}`,
          value: earnValue - 500 + i * 45,
          timestamp: `Week ${i + 1}`
        };
      }),
      '6M': Array.from({
        length: 24
      }, (_, i) => {
        const earnValue = getAccountValue('earn');
        return {
          time: `Week ${i + 1}`,
          value: earnValue - 1000 + i * 45,
          timestamp: `Week ${i + 1}`
        };
      }),
      '1Y': Array.from({
        length: 12
      }, (_, i) => {
        const earnValue = getAccountValue('earn');
        return {
          time: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
          value: earnValue - 2000 + i * 180,
          timestamp: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]
        };
      }),
      All: Array.from({
        length: 24
      }, (_, i) => {
        const earnValue = getAccountValue('earn');
        return {
          time: `M${i + 1}`,
          value: earnValue * 0.3 + earnValue * 0.7 * i / 23,
          timestamp: `Month ${i + 1}`
        };
      })
    }
  };
  return baseData[accountType as keyof typeof baseData] || baseData.main;
};
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return <div style={{
    background: 'rgba(20, 22, 32, 0.95)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '12px 16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
  }} data-id="element-1772">
      <div style={{
      color: '#9CA3AF',
      fontSize: '10px',
      marginBottom: '6px',
      letterSpacing: '0.05em',
      fontWeight: '400',
      textTransform: 'uppercase'
    }} data-id="element-1773">
        {data.timestamp}
      </div>

      <div style={{
      color: '#FFFFFF',
      fontSize: '18px',
      fontWeight: '500',
      letterSpacing: '-0.01em'
    }} data-id="element-1774">
        {data.value.toFixed(2)} CHF
      </div>

      {data.upnl !== undefined && <div style={{
      color: data.upnl >= 0 ? '#00C076' : '#E85D75',
      fontSize: '12px',
      marginTop: '4px'
    }} data-id="element-1775">
          UP&L: {data.upnl >= 0 ? '+' : ''}
          {data.upnl.toFixed(5)} CHF
        </div>}
    </div>;
};
export function PortfolioChart({
  accountType
}: PortfolioChartProps) {
  const [timeRange, setTimeRange] = useState<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'>('1W');
  const [chartType, setChartType] = useState<'value' | 'pnl' | 'portfolio'>('portfolio');
  const chartData = useMemo(() => {
    const data = generateChartData(accountType);
    return data[timeRange] || data['1W'];
  }, [accountType, timeRange]);
  const ranges: Array<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'> = ['1W', '1M', '3M', '6M', '1Y', 'All'];
  // Use actual account value from portfolioData
  const getTotalValue = () => {
    return getAccountValue(accountType);
  };
  const getChange = () => {
    if (chartData.length < 2) return {
      amount: 0,
      percent: 0
    };
    const first = chartData[0].value;
    const last = chartData[chartData.length - 1].value;
    const amount = last - first;
    const percent = amount / first * 100;
    return {
      amount,
      percent
    };
  };
  const change = getChange();
  return <div className="glass-border rounded-[20px] p-6" data-id="element-1776">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6" data-id="element-1777">
        <div data-id="element-1778">
          <div className="text-sm text-text-secondary mb-1 font-label uppercase" data-id="element-1779">
            Total value
          </div>
          <div className="flex items-baseline gap-2 mb-1" data-id="element-1780">
            <h2 className="text-3xl font-light text-text-primary tracking-tight font-numbers" data-id="element-1781">
              {getTotalValue().toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            </h2>
            <span className="text-lg text-text-secondary font-normal" data-id="element-1782">USD</span>
          </div>
          <span className={`flex items-center gap-1 text-sm font-medium ${change.amount >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1783">
            {change.amount >= 0 ? '+' : ''}
            {change.amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}{' '}
            USD ({change.amount >= 0 ? '+' : ''}
            {change.percent.toFixed(2)}%){' '}
            <span className="text-text-secondary" data-id="element-1784">last {timeRange}</span>
          </span>
        </div>

        {/* Consistent Timeframe Selector - Top Right */}
        <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 -mx-2 px-2 md:mx-0 md:px-0" data-id="element-1785">
          <div className="flex items-center gap-2 bg-background-elevated p-1 rounded-lg w-max md:w-auto" data-id="element-1786">
            {ranges.map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${timeRange === range ? 'bg-background-elevated text-text-primary border-border-default' : 'text-text-secondary border-transparent hover:text-text-primary hover:bg-white/5'}`} data-id="element-1787">
                {range}
              </button>)}
          </div>
        </div>
      </div>

      {/* Chart Type Toggles */}
      {accountType === 'main' && <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2 -mx-2 px-2 md:mx-0 md:px-0 md:pb-0" data-id="element-1788">
          <button onClick={() => setChartType('value')} className={`whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${chartType === 'value' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1789">
            Value
          </button>
          <button onClick={() => setChartType('pnl')} className={`whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${chartType === 'pnl' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1790">
            P&L
          </button>
          <button onClick={() => setChartType('portfolio')} className={`whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${chartType === 'portfolio' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1791">
            Portfolio value
          </button>
        </div>}

      {/* Chart */}
      <div className="relative w-full h-[200px] md:h-[300px]" data-id="element-1792">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1793">
          {chartType === 'portfolio' && accountType === 'main' ? <ComposedChart data={chartData} margin={{
          left: 0,
          right: 0,
          top: 10,
          bottom: 0
        }} data-id="element-1794">
              <defs data-id="element-1795">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-1796">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-1797" />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-1798" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-1799" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
            fill: '#848E9C',
            fontSize: 11
          }} dy={10} data-id="element-1800" />
              <YAxis hide domain={['dataMin - 0.5', 'dataMax + 0.5']} data-id="element-1801" />
              <Tooltip content={<CustomTooltip data-id="element-1803" />} cursor={{
            stroke: '#8B5CF6',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-1802" />
              <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" data-id="element-1804" />
              <Line type="monotone" dataKey="upnl" stroke="#E85D75" strokeWidth={1.5} dot={false} strokeDasharray="5 5" data-id="element-1805" />
            </ComposedChart> : <AreaChart data={chartData} margin={{
          left: 0,
          right: 0,
          top: 10,
          bottom: 0
        }} data-id="element-1806">
              <defs data-id="element-1807">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-1808">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-1809" />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-1810" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-1811" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
            fill: '#848E9C',
            fontSize: 11
          }} dy={10} data-id="element-1812" />
              <YAxis hide domain={['dataMin - 0.5', 'dataMax + 0.5']} data-id="element-1813" />
              <Tooltip content={<CustomTooltip data-id="element-1815" />} cursor={{
            stroke: '#8B5CF6',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-1814" />
              <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" data-id="element-1816" />
            </AreaChart>}
        </ResponsiveContainer>
      </div>

      {/* Chart Legend for Portfolio view */}
      {chartType === 'portfolio' && accountType === 'main' && <div className="flex items-center gap-4 mt-4 text-xs" data-id="element-1817">
          <div className="flex items-center gap-2" data-id="element-1818">
            <div className="w-3 h-3 rounded-full bg-brand-purple" data-id="element-1819"></div>
            <span className="text-text-secondary" data-id="element-1820">Margin UP&L</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1821">
            <div className="w-3 h-0.5 bg-danger" data-id="element-1822"></div>
            <span className="text-text-secondary" data-id="element-1823">Positions UP&L</span>
          </div>
        </div>}
    </div>;
}